<template>
  <section v-if="!pending">
    <BaseLink
      :address="{ linktype: 'story', cached_url: articleData.full_slug }"
      target="_blank"
      class="read-also"
    >
      <BaseNuxtImage class="read-also__image" :src="src" :alt="alt" />
      <div class="read-also__content">
        <h4 class="read-also__title">
          {{ articleData.content && articleData.content.meta.title }}
        </h4>
        <p class="read-also__description">
          {{ articleData.content && articleData.content.meta.description }}
        </p>
        <p class="read-also__link">Read the article →</p>
      </div>
    </BaseLink>
  </section>
</template>

<script lang="ts" setup>
import type { SbBlogReadAlsoStoryblok } from '@/types'
// TODO: change this component to use store

interface Props {
  blok: SbBlogReadAlsoStoryblok
}

const props = defineProps<Props>()
const storyblokApiInstance = useStoryblokApi()

const { locale } = useI18n()
const config = useRuntimeConfig()

const language = computed(() => (locale.value === 'en' ? '' : locale.value))

const { data: articleData, pending } = await useLazyAsyncData(
  'read-more',
  async () =>
    await storyblokApiInstance.get(`cdn/stories/${props.blok.article}`, {
      language: language.value,
      version: config.public.storyblokApiVersion as 'draft' | 'published',
      find_by: 'uuid',
    }),
  { transform: (data) => data.data.story },
)
const src = computed(() => articleData.value.content?.meta.og_image || '')
const alt = computed(() => articleData.value.content?.meta.og_image_alt || '')
</script>
<style lang="scss" scoped>
.read-also {
  $root: &;

  display: flex;
  flex-direction: column;
  gap: 32px;
  cursor: pointer;
  padding: 48px;
  align-items: flex-start;
  color: var(--cobalt-800);
  text-decoration: none;
  border: 1px solid var(--gray-300);
  border-radius: 16px;

  @media (min-width: $breakpoint-md) {
    flex-direction: row;
  }

  &__image {
    margin-top: 12px;
    width: 100%;
    max-width: 200px;
    height: auto;
    border-radius: 8px;
  }

  &__text {
    @include paragraph-s;

    margin-bottom: 8px;
  }

  &__content {
    flex-grow: 1;
  }

  &__title {
    margin-bottom: 8px;
  }

  &__description {
    margin-bottom: 8px;
  }

  &__link {
    margin: 0;
    color: var(--color-link);
  }

  &:hover {
    #{$root}__link {
      text-decoration: underline;
    }

    #{$root}__title {
      color: var(--color-link);
    }
  }
}
</style>
